import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag, Loader2, Star, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { createCheckout, customerMe, customerRewards, validateCoupon, validateGiftCard, fetchSettings } from '../api';

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, clear } = useCart();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cust, setCust] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [applyPts, setApplyPts] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [giftInput, setGiftInput] = useState('');
  const [gift, setGift] = useState(null);
  const [giftErr, setGiftErr] = useState('');
  const [giftLoading, setGiftLoading] = useState(false);
  const [feeCfg, setFeeCfg] = useState(null);

  useEffect(() => { fetchSettings().then((s) => setFeeCfg(s?.processingFee || null)).catch(() => {}); }, []);

  const applyGift = async () => {
    const code = giftInput.trim();
    if (!code || giftLoading) return;
    setGiftLoading(true); setGiftErr('');
    try {
      const res = await validateGiftCard(code);
      if (res.valid) { setGift(res); toast({ title: 'Gift card applied \u2728', description: `$${res.balance.toFixed(2)} available.` }); }
      else { setGift(null); setGiftErr(res.message || 'Invalid gift card.'); }
    } catch { setGiftErr('Could not validate gift card.'); }
    setGiftLoading(false);
  };
  const removeGift = () => { setGift(null); setGiftInput(''); setGiftErr(''); };

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code || couponLoading) return;
    setCouponLoading(true); setCouponErr('');
    try {
      const res = await validateCoupon(code, subtotal);
      if (res.valid) { setCoupon(res); toast({ title: 'Code applied \u2728', description: `−$${res.discount.toFixed(2)} off your ritual.` }); }
      else { setCoupon(null); setCouponErr(res.message || 'Invalid code.'); }
    } catch { setCouponErr('Could not validate code.'); }
    setCouponLoading(false);
  };

  const removeCoupon = () => { setCoupon(null); setCouponInput(''); setCouponErr(''); };

  useEffect(() => {
    if (isOpen && localStorage.getItem('pe_cust_token')) {
      customerMe().then((m) => setCust(m.customer)).catch(() => setCust(null));
      customerRewards().then(setRewards).catch(() => setRewards(null));
    }
  }, [isOpen]);

  // Re-validate an applied coupon whenever the subtotal changes
  useEffect(() => {
    if (!coupon) return;
    validateCoupon(coupon.code, subtotal).then((res) => {
      if (res.valid) setCoupon(res);
      else { setCoupon(null); setCouponErr(res.message || 'Code no longer valid.'); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const redeemablePts = rewards && rewards.points >= rewards.min_redeem
    ? Math.min(rewards.points, rewards.max_per_order || rewards.points, Math.floor(subtotal / rewards.point_value))
    : 0;
  const ptsDiscount = rewards ? +(redeemablePts * rewards.point_value).toFixed(2) : 0;
  const couponDisc = coupon ? coupon.discount : 0;
  const giftDisc = gift ? Math.min(gift.balance, Math.max(0, subtotal - (applyPts ? ptsDiscount : 0) - couponDisc)) : 0;
  const estTotal = Math.max(0, subtotal - (applyPts ? ptsDiscount : 0) - couponDisc - giftDisc);
  const procFee = feeCfg && feeCfg.enabled && estTotal > 0
    ? Math.ceil(((estTotal + Number(feeCfg.fixed || 0)) / (1 - Number(feeCfg.pct || 0) / 100) - estTotal) * 100) / 100
    : 0;
  const estWithFee = +(estTotal + procFee).toFixed(2);

  const checkout = async () => {
    if (loading || items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ id: i.id, qty: i.qty, size: i.size || null })),
        origin_url: window.location.origin,
        ref_code: localStorage.getItem('pe_ref') || null,
        customer_id: cust ? cust.id : null,
        email: cust ? cust.email : null,
        redeem_points: applyPts && cust ? redeemablePts : 0,
        coupon_code: coupon ? coupon.code : null,
        gift_card_code: gift ? gift.code : null,
      };
      const res = await createCheckout(payload);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (e) {
      setLoading(false);
      toast({ title: 'Checkout error', description: e?.response?.data?.detail || 'We could not start checkout. Please try again.' });
    }
  };

  return (
    <>
      <div
        onClick={() => setIsOpen(false)}
        className={`fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      />
      <aside className={`fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-[#0d0a0c] border-l border-[#d4af37]/25 shadow-2xl transition-transform duration-500 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#d4af37]/20">
          <h3 className="font-cinzel tracking-[0.2em] text-gold-gradient text-lg">YOUR RITUAL</h3>
          <button onClick={() => setIsOpen(false)} className="text-[#f5ecd9]/70 hover:text-[#ff4da6] transition-colors"><X className="h-6 w-6" /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[70%] text-center px-8">
            <ShoppingBag className="h-12 w-12 text-[#d4af37]/50 mb-4" />
            <p className="font-cormorant text-2xl text-[#f5ecd9]/70">Your cart is empty.</p>
            <p className="font-jost text-sm text-[#f5ecd9]/40 mt-2 mb-6">Discover treasures to radiate your best version.</p>
            <Link to="/shop" onClick={() => setIsOpen(false)} className="btn-gold rounded-md px-8 py-3 text-sm font-medium text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]">Shop Now</Link>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto px-6 py-4 space-y-4" style={{ maxHeight: 'calc(100% - 220px)' }}>
              {items.map((i) => (
                <div key={i.id} className="flex gap-4 card-glass rounded-lg p-3">
                  <img src={i.image} alt={i.name} className="h-20 w-20 rounded-md object-cover" />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h4 className="font-cormorant text-lg text-[#f5ecd9] leading-tight">{i.name}</h4>
                      <button onClick={() => removeItem(i.id)} className="text-[#f5ecd9]/40 hover:text-[#ff4da6]"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <p className="text-[#f0d582] font-jost text-sm mt-1">${i.price.toFixed(2)}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => updateQty(i.id, i.qty - 1)} className="h-7 w-7 rounded-full border border-[#d4af37]/30 flex items-center justify-center text-[#f0d582] hover:bg-[#d4af37]/10"><Minus className="h-3 w-3" /></button>
                      <span className="font-jost text-sm w-6 text-center">{i.qty}</span>
                      <button onClick={() => updateQty(i.id, i.qty + 1)} className="h-7 w-7 rounded-full border border-[#d4af37]/30 flex items-center justify-center text-[#f0d582] hover:bg-[#d4af37]/10"><Plus className="h-3 w-3" /></button>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={clear} className="text-xs text-[#f5ecd9]/40 hover:text-[#ff4da6] underline">Clear cart</button>
            </div>

            <div className="absolute bottom-0 left-0 right-0 border-t border-[#d4af37]/20 bg-[#0d0a0c] px-6 py-5">
              <div className="flex justify-between mb-1 font-jost text-sm text-[#f5ecd9]/70"><span>Subtotal</span><span className="text-[#f0d582]">${subtotal.toFixed(2)}</span></div>
              {cust && rewards && redeemablePts > 0 && (
                <label className="flex items-center justify-between gap-2 mb-2 p-3 rounded-lg border border-[#d4af37]/25 bg-[#d4af37]/5 cursor-pointer">
                  <span className="flex items-center gap-2 font-jost text-xs text-[#f5ecd9]/80"><Star className="h-4 w-4 text-[#f0d582]" /> Apply {redeemablePts} points (−${ptsDiscount.toFixed(2)})</span>
                  <input data-testid="apply-points-checkbox" type="checkbox" checked={applyPts} onChange={(e) => setApplyPts(e.target.checked)} />
                </label>
              )}
              {cust && applyPts && (<div className="flex justify-between mb-1 font-jost text-sm text-[#2ecc71]"><span>Rewards</span><span>−${ptsDiscount.toFixed(2)}</span></div>)}
              {!coupon ? (
                <div className="mb-2">
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-2 rounded-lg border border-[#d4af37]/25 bg-white/5 px-3">
                      <Tag className="h-4 w-4 text-[#f0d582]" />
                      <input data-testid="coupon-input" value={couponInput} onChange={(e) => { setCouponInput(e.target.value); setCouponErr(''); }} onKeyDown={(e) => e.key === 'Enter' && applyCoupon()} placeholder="Promo code" className="flex-1 bg-transparent py-2.5 text-sm text-[#f5ecd9] placeholder:text-[#f5ecd9]/30 focus:outline-none uppercase" />
                    </div>
                    <button data-testid="coupon-apply-btn" onClick={applyCoupon} disabled={couponLoading || !couponInput.trim()} className="rounded-lg border border-[#d4af37]/40 px-4 text-xs font-jost uppercase tracking-widest text-[#f0d582] hover:bg-[#d4af37]/10 disabled:opacity-40">{couponLoading ? '…' : 'Apply'}</button>
                  </div>
                  {couponErr && <p data-testid="coupon-error" className="text-[#ff4da6] text-xs mt-1 font-jost">{couponErr}</p>}
                </div>
              ) : (
                <div data-testid="coupon-applied" className="flex justify-between items-center mb-2 p-2.5 rounded-lg border border-[#2ecc71]/30 bg-[#2ecc71]/5">
                  <span className="flex items-center gap-2 font-jost text-xs text-[#2ecc71]"><Tag className="h-3.5 w-3.5" /> {coupon.code} applied</span>
                  <span className="flex items-center gap-3"><span className="font-jost text-sm text-[#2ecc71]">−${coupon.discount.toFixed(2)}</span><button onClick={removeCoupon} className="text-[#f5ecd9]/40 hover:text-[#ff4da6]"><X className="h-3.5 w-3.5" /></button></span>
                </div>
              )}
              {!gift ? (
                <div className="mb-2">
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-2 rounded-lg border border-[#d4af37]/25 bg-white/5 px-3">
                      <Tag className="h-4 w-4 text-[#f0d582]" />
                      <input data-testid="gift-input" value={giftInput} onChange={(e) => { setGiftInput(e.target.value.toUpperCase()); setGiftErr(''); }} onKeyDown={(e) => e.key === 'Enter' && applyGift()} placeholder="Gift card code" className="flex-1 bg-transparent py-2.5 text-sm text-[#f5ecd9] placeholder:text-[#f5ecd9]/30 focus:outline-none uppercase" />
                    </div>
                    <button data-testid="gift-apply-btn" onClick={applyGift} disabled={giftLoading || !giftInput.trim()} className="rounded-lg border border-[#d4af37]/40 px-4 text-xs font-jost uppercase tracking-widest text-[#f0d582] hover:bg-[#d4af37]/10 disabled:opacity-40">{giftLoading ? '…' : 'Apply'}</button>
                  </div>
                  {giftErr && <p data-testid="gift-error" className="text-[#ff4da6] text-xs mt-1 font-jost">{giftErr}</p>}
                </div>
              ) : (
                <div data-testid="gift-applied" className="flex justify-between items-center mb-2 p-2.5 rounded-lg border border-[#2ecc71]/30 bg-[#2ecc71]/5">
                  <span className="flex items-center gap-2 font-jost text-xs text-[#2ecc71]"><Tag className="h-3.5 w-3.5" /> Gift card applied</span>
                  <span className="flex items-center gap-3"><span className="font-jost text-sm text-[#2ecc71]">−${giftDisc.toFixed(2)}</span><button onClick={removeGift} className="text-[#f5ecd9]/40 hover:text-[#ff4da6]"><X className="h-3.5 w-3.5" /></button></span>
                </div>
              )}
              {procFee > 0 && (
                <div className="flex justify-between mb-1 font-jost text-sm text-[#f5ecd9]/70" data-testid="cart-processing-fee"><span className="flex items-center gap-1">{feeCfg?.label || 'Service & Processing Fee'}</span><span className="text-[#f0d582]">${procFee.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between mb-1 font-jost text-sm text-[#f5ecd9] border-t border-white/10 pt-2"><span>{procFee > 0 ? 'Estimated total (before tax)' : 'Estimated total'}</span><span className="text-gold-gradient font-cinzel" data-testid="cart-estimated-total">${estWithFee.toFixed(2)}</span></div>
              {!cust && (<p className="text-xs text-[#f0d582]/80 mb-2"><Link to="/account" onClick={() => setIsOpen(false)} className="underline">Log in</Link> to earn & redeem Prestige Rewards points.</p>)}
              <p className="text-xs text-[#f5ecd9]/40 mb-4">{procFee > 0 ? 'A service & processing fee applies. ' : ''}Sales tax is calculated by Stripe at checkout based on your address. Free shipping over $50.</p>
              <button data-testid="cart-checkout-btn" onClick={checkout} disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2 rounded-md py-3.5 text-sm font-semibold tracking-wide text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37] disabled:opacity-60">{loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : 'Checkout'}</button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
